import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Circle, ArrowRight, X, Sparkles } from 'lucide-react';

const OnboardingChecklist = ({ onDismiss }) => {
  const navigate = useNavigate();
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    // Iter-192.1 — session-scoped dismissal so completed prompts don't keep
    // returning on every tab switch.
    try {
      return sessionStorage.getItem('onboarding_checklist_dismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('onboarding_checklist_dismissed', '1'); } catch { /* ignore */ }
    onDismiss?.();
  };

  useEffect(() => {
    fetchCompletion();
    // Re-fetch whenever any profile panel signals a successful save
    const handler = () => fetchCompletion();
    window.addEventListener('talent-profile-updated', handler);
    return () => window.removeEventListener('talent-profile-updated', handler);
  }, []);

  const fetchCompletion = async () => {
    try {
      const res = await api.get('/home/profile-completion');
      setCompletion(res.data);
      // If profile is complete, auto-dismiss
      if (res.data.complete) {
        setDismissed(true);
      }
    } catch (e) {
      console.error('Failed to fetch profile completion:', e);
      setDismissed(true); // Hide if error
    } finally {
      setLoading(false);
    }
  };

  if (loading || dismissed || !completion || completion.complete) {
    return null;
  }

  // Iter-192.1 — Show ONLY pending items so completed prompts don't take up
  // dashboard real estate. Progress bar still shows overall completion.
  const pendingItems = (completion.checklist || []).filter((it) => !it.complete);

  return (
    <Card className="bg-gradient-to-r from-cyan-500/10 to-fuchsia-500/10 border-cyan-200" data-testid="onboarding-checklist">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-cyan-500" />
              <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                {pendingItems.length === 0
                  ? "All set — you're ready to get booked"
                  : `Finish ${pendingItems.length} more step${pendingItems.length === 1 ? '' : 's'} to start getting booked`}
              </h3>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">Profile completion</span>
                <span className="font-medium text-cyan-600">{completion.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-fuchsia-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${completion.percent}%` }}
                />
              </div>
            </div>

            {/* Pending-only checklist — completed items are removed entirely */}
            {pendingItems.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {pendingItems.map((item) => (
                  <button
                    key={item.item}
                    onClick={() => navigate(item.path)}
                    className="flex items-center gap-2 p-2 rounded-lg text-left text-sm bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 transition-colors"
                    data-testid={`checklist-${item.item}`}
                  >
                    <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => navigate('/talent/profile-info')}
                className="bg-cyan-500 hover:bg-cyan-600"
                size="sm"
              >
                Complete Profile <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <span className="text-xs text-gray-500">
                {completion.completed_count} of {completion.total_count} steps complete
              </span>
            </div>
          </div>

          {/* Iter-192.1 — Dismiss is always available so talent can collapse
              the card once they understand what's pending. */}
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
            aria-label="Dismiss checklist"
            data-testid="onboarding-checklist-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
